import { Label } from "@/components/ui/label";
import { TypeAddInput } from "./TypeAddInput";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X, Tag, Plus, Minus } from "lucide-react";
import { ManagedCampaignData } from "./ManagedCampaignData";
import { useState } from "react";

interface ManagedKeywordsStepProps {
    data: ManagedCampaignData;
    updateData: (updates: Partial<ManagedCampaignData>) => void;
}

type KeywordField = "companyKeywords" | "personKeywords" | "excludeCompanyKeywords" | "excludePersonKeywords";

export const ManagedKeywordsStep = ({ data, updateData }: ManagedKeywordsStepProps) => {
    const [inputs, setInputs] = useState<Record<KeywordField, string>>({
        companyKeywords: "",
        personKeywords: "",
        excludeCompanyKeywords: "",
        excludePersonKeywords: "",
    });

    const getList = (field: KeywordField): string[] => (data[field] as string[] | undefined) || [];

    const removeKeyword = (field: KeywordField, index: number) => {
        const next = [...getList(field)];
        next.splice(index, 1);
        updateData({ [field]: next } as Partial<ManagedCampaignData>);
    };

    const addKeyword = (field: KeywordField) => {
        const value = inputs[field].trim();
        if (!value) return;
        const current = getList(field);
        if (!current.includes(value)) {
            updateData({ [field]: [...current, value] } as Partial<ManagedCampaignData>);
        }
        setInputs(prev => ({ ...prev, [field]: "" }));
    };

    const handleKeyDown = (field: KeywordField, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            addKeyword(field);
        }
    };

    const renderSection = (
        label: string,
        field: KeywordField,
        variant: "include" | "exclude",
    ) => {
        const list = getList(field);
        const isExclude = variant === "exclude";
        return (
            <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                    {isExclude ? (
                        <Minus className="w-3.5 h-3.5 text-destructive" />
                    ) : (
                        <Plus className="w-3.5 h-3.5 text-emerald-600" />
                    )}
                    <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {label}
                    </Label>
                </div>
                {list.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {list.map((keyword, i) => (
                            <Badge
                                key={i}
                                variant={isExclude ? "destructive" : "default"}
                                className={`pl-3 pr-2 py-1.5 flex items-center gap-1 font-normal ${isExclude ? "" : "bg-success text-success-foreground hover:bg-success/90 border-transparent shadow-sm"}`}
                            >
                                {isExclude ? `− ${keyword}` : keyword}
                                <button
                                    onClick={() => removeKeyword(field, i)}
                                    className="opacity-80 hover:opacity-100"
                                    aria-label={`Remove ${keyword}`}
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </Badge>
                        ))}
                    </div>
                )}
                <TypeAddInput placeholder={isExclude ? "Type a keyword to exclude" : "Type a keyword to include"} value={inputs[field]} onChange={(v) => setInputs(prev => ({ ...prev, [field]: v }))} onKeyDown={(e) => handleKeyDown(field, e)} onAdd={() => addKeyword(field)} className="max-w-none" />
            </div>
        );
    };

    return (
        <div className="space-y-6 animate-in fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Company Keywords */}
                <Card>
                    <CardContent className="pt-6 space-y-5">
                        <div className="flex items-center gap-2">
                            <Tag className="w-4 h-4 text-muted-foreground" />
                            <Label className="text-base font-semibold">
                                Company Keywords <span className="text-muted-foreground text-xs font-normal">(optional)</span>
                            </Label>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Filter companies by keywords. Use exclude to skip companies matching certain terms.
                        </p>
                        {renderSection("Include", "companyKeywords", "include")}
                        {renderSection("Exclude", "excludeCompanyKeywords", "exclude")}
                    </CardContent>
                </Card>

                {/* Person Keywords */}
                <Card>
                    <CardContent className="pt-6 space-y-5">
                        <div className="flex items-center gap-2">
                            <Tag className="w-4 h-4 text-muted-foreground" />
                            <Label className="text-base font-semibold">
                                Person Keywords <span className="text-muted-foreground text-xs font-normal">(optional)</span>
                            </Label>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Filter people by keywords. Use exclude to skip people matching certain terms.
                        </p>
                        {renderSection("Include", "personKeywords", "include")}
                        {renderSection("Exclude", "excludePersonKeywords", "exclude")}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
